import React from "react"
import type { UserInfo } from "#/lib/carrot-api/types/UserInfo"
import useMemberQuery from "#/services/user/member-info"
import { AEONIK_FONT_URLS } from "#/fonts/aeonikFontUrls"

// The consumer subscription funnel's public surface, plus the signed-in member's
// carrot-app profile that its details step prefills from.
//
// The profile is read with `useMemberQuery` — `getCurrentUser` on the shared `["member"]`
// react-query key — rather than threaded down from redux `userInfo`. It is the SAME
// endpoint either way (redux's own `fetchUserInfo` calls `getCurrentUser` too), but this
// keeps the funnel's host contract off the `state.userInfo` shape and reads through
// `UserInfo`, which is typed where the `(state: any) => any` selectors were not.
//
// It lives HERE, in the host, rather than inside `@internal/consumer`: the package never
// reaches into `#/…`, so the host resolves session data and hands it in as props — the
// same rule `isLoggedIn` and `email` already follow.
//
// ⚠️ The Stripe publishable key is the one host value the funnel deliberately does NOT take:
// `window.env.STRIPE_PUBLISHABLE_KEY` is the ENTERPRISE key (Carrot Card, inbound member
// payments), and passing it in is what broke production checkout on 2026-09-11 — a live key
// against a test-mode PaymentIntent. The funnel reads the consumer key from the BFF instead.
//
// The lazy import stays in this file so the package is still code-split: nothing from
// `@internal/consumer` loads until someone navigates to a `/consumer` path.
const ConsumerSubscriptionRoute = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerSubscriptionRoute }))
)

type ConsumerSubscriptionRouteProps = {
  isLoggedIn: boolean
  email?: string
  selfServeSignupEnabled?: boolean
  onLogin?: (email: string, password: string) => Promise<void>
  onProbeAppAccess?: () => Promise<boolean>
  onExitToApp?: () => void
}

/**
 * The funnel's prefill fields, carrot-app-native: `state` is the 2-letter USPS
 * abbreviation and `dateOfBirth` is "YYYY-MM-DD". `UserInfo` nulls each optional field
 * while the funnel's contract takes `?: string`, so nulls are mapped to undefined —
 * otherwise a `null` would override the funnel's own value in its `funnel ?? profile`
 * prefill chain instead of falling through to it.
 */
function selectFunnelProfile(user: UserInfo) {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    dateOfBirth: user.dateOfBirth ?? undefined,
    state: user.state ?? undefined,
    zip: user.zip ?? undefined,
    // Assigned sex at birth as carrot-app stores it — the same enum values the funnel's
    // own option list uses (MALE / FEMALE / INTERSEX), so it prefills verbatim.
    sex: user.sex ?? undefined
  }
}

function SignedInConsumerSubscriptionRoute(props: ConsumerSubscriptionRouteProps): JSX.Element {
  // `data` is undefined until the query resolves, which the funnel already treats as
  // "no profile to prefill from" — the same state redux left it in while user info was
  // still loading.
  //
  // `isLoading` is handed down with it because that reading is ambiguous where it counts:
  // the funnel's checkout write-back has to tell "this member has no stored sex" from
  // "the answer hasn't arrived yet" before it decides whether to preserve one
  // (CONAPP-415). It is false once the query settles, including when it errors — a failed
  // read is an answer we are never going to improve on, and blocking the write-back on it
  // forever would drop the whole sync.
  //
  // `isError` is the THIRD reading, and it is the one an in-flight flag alone cannot
  // express: the read settled, so nothing is coming, but the empty `profile` says nothing
  // about what carrot-app has stored. Treating that as "no stored value" is what lets the
  // write-back overwrite an INTERSEX it never saw, so the funnel is told the read failed
  // and holds `sex` back rather than guessing.
  const { data: profile, isLoading, isError } = useMemberQuery(selectFunnelProfile)
  return (
    <ConsumerSubscriptionRoute
      {...props}
      profile={profile}
      profileLoading={isLoading}
      profileFailed={isError}
      aeonikFontUrls={AEONIK_FONT_URLS}
    />
  )
}

export function ConsumerSubscriptionRouteWithProfile(props: ConsumerSubscriptionRouteProps): JSX.Element {
  // `getCurrentUser` is an AUTHENTICATED endpoint and this route is mounted for signed-out
  // visitors too (the funnel is public), so the query has to sit in a child that only
  // mounts once there is a session: `useMemberQuery` takes no `enabled` option, and a hook
  // can't be called conditionally. Without the split, every anonymous funnel visit would
  // fire a request that can only 401.
  return props.isLoggedIn ? (
    <SignedInConsumerSubscriptionRoute {...props} />
  ) : (
    <ConsumerSubscriptionRoute {...props} aeonikFontUrls={AEONIK_FONT_URLS} />
  )
}
